import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { PlanningDate } from "../nexusFilters";
import { Job, WebSocketMessagePayload } from "../../hooks/WebSocketProvider";
import api, { API_PATHS } from "../../service/api";
import { PlanViewState, PlanViewStateEntryStatus, SolutionType } from "./index";
import { latestMessageIsOneOf, messageStreamIncludesType } from "./helpers";
import { createPlanViewStateKey, serializeKey } from "./stateKeyUtils";

const determineNexusStatusFromWebSocketMessages = (
  websocketMessages: WebSocketMessagePayload[],
): PlanViewStateEntryStatus => {
  const hasNexusError = messageStreamIncludesType(
    "NexusLandingProgressDataRetrievalFailedMessage",
    websocketMessages,
  );
  const isLoading = latestMessageIsOneOf(
    [
      "NexusLandingInitiatedMessage",
      "NexusLandingProgressDataRetrievalInitiatedMessage",
      "NexusLandingDataRetrievalAlreadyRunningMessage",
      "NexusLandingSupersededMessage",
    ],
    websocketMessages,
  );
  const isReady = messageStreamIncludesType(
    "NexusLandingProgressNexusDataReadyMessage",
    websocketMessages,
  );

  if (hasNexusError) return "error";

  if (isLoading) return "loading";

  if (isReady) return "ready";

  return "missing";
};

const determinePondooStatusFromWebSocketMessages = (
  websocketMessages: WebSocketMessagePayload[],
): PlanViewStateEntryStatus => {
  const isLoading = latestMessageIsOneOf(
    [
      "NexusLandingInitiatedMessage",
      "NexusLandingProgressOptimizationInitiated",
      "NexusLandingProgressNexusDataReadyMessage",
      "NexusLandingSupersededMessage",
    ],
    websocketMessages,
  );
  const isReady = messageStreamIncludesType(
    "NexusLandingProgressOptimizationFinished",
    websocketMessages,
  );
  if (isLoading) return "loading";
  if (isReady) return "ready";

  return "missing";
};

/**
 * jobRef of the run the newest message belongs to. A superseded message is sent for the replaced run, after the
 * newer run has started, so it points at its successor instead.
 */
const latestRunJobRef = (
  messages?: WebSocketMessagePayload[],
): string | undefined => {
  const latest = messages?.[messages.length - 1];
  if (latest?.bridgeMessageType === "NexusLandingSupersededMessage")
    return latest.data.supersededByJobRef;
  return latest?.jobId;
};

const jobEntityIsOutdated = (
  job?: Job,
  messages?: WebSocketMessagePayload[],
) => {
  const latest = latestRunJobRef(messages);
  return !!job && !!latest && job.jobRef !== latest;
};

/**
 * A landing that still fetches from Nexus has no job yet. It replaces both plans, so the cell's existing
 * solutions must not make either view "ready" meanwhile.
 */
const activeRunIsFetching = (data: StateObject): boolean =>
  latestMessageIsOneOf(
    [
      "NexusLandingInitiatedMessage",
      "NexusLandingProgressDataRetrievalInitiatedMessage",
      "NexusLandingDataRetrievalAlreadyRunningMessage",
      "NexusLandingSupersededMessage",
    ],
    data.websocketMessages,
  ) && data.job?.jobRef !== latestRunJobRef(data.websocketMessages);

const determineStatusFromJobAndWebSocketMessages = (
  solutionType: SolutionType,
  data: StateObject,
): PlanViewStateEntryStatus => {
  if (activeRunIsFetching(data)) return "loading";

  if (
    solutionType === "NexusSolution" &&
    !!data.planningDate?.nexusIdSolution
  ) {
    // For the Nexus view specifically,
    // If we have a NexusIdSolution, then we can disregard state involving the optimization process
    return "ready";
  }

  if (
    solutionType === "PondooSolution" &&
    !!data.planningDate?.pondooIdSolution
  ) {
    // For the Pondoo view specifically,
    // If we have a pondooIdSolution, then we can disregard state involving the optimization process
    return "ready";
  }

  const { job, websocketMessages } = data;

  if (!job || jobEntityIsOutdated(job, websocketMessages)) {
    // A retrieval might be underway
    if (!websocketMessages) {
      return "missing";
    }

    if (job?.jobStatus === "Completed") return "ready";

    return solutionType === "NexusSolution"
      ? determineNexusStatusFromWebSocketMessages(websocketMessages)
      : determinePondooStatusFromWebSocketMessages(websocketMessages);
  }

  return {
    Completed: "ready",
    Running: "loading",
    Scheduled: "loading",
    Failed: "error",
  }[job.jobStatus] as PlanViewStateEntryStatus;
};

const getPondooValueData = (
  messages: WebSocketMessagePayload[],
  job?: Job,
): { job?: Job; idScenario?: number } => {
  messages = messages ?? [];
  const returnJob =
    job ??
    (messages.find(
      (x) =>
        x.bridgeMessageType === "NexusLandingProgressOptimizationInitiated",
    )?.data as Job | undefined);
  return { job: returnJob, idScenario: returnJob?.idScenario };
};

// Only the active run's job may drive the status bar: an earlier run's job would count time from the wrong start
const jobOfRun = (data: StateObject, jobRef?: string): Job | undefined =>
  data.job?.jobRef === jobRef
    ? data.job
    : (data.websocketMessages?.find(
        (x) =>
          x.bridgeMessageType === "NexusLandingProgressOptimizationInitiated" &&
          x.jobId === jobRef,
      )?.data as Job | undefined);

// When the job entity is outdated the newest message stream belongs to a newer run than the job.
const findActiveJobRef = (data: StateObject): string | undefined =>
  latestRunJobRef(data.websocketMessages) ?? data.job?.jobRef;

// Refactored
type StateObject = {
  job?: Job;
  websocketMessages: WebSocketMessagePayload[];
  planningDate?: PlanningDate;
};
type FullStatePayload = Record<number, Record<string, StateObject>>;

/**
 * Hydrates the state of the plan view (gantt chart) history by combining all the way state can be stored at
 * a particular moment: PlanningDates (historical jobs), webSocketJobs (ongoing jobs), and jobEntities.
 * <br>
 * We use the Job (job entity) to set the timeAgo property on the status bar.
 * @param state
 * @param action
 */
export const hydrateFullPlanState = createAsyncThunk(
  "hydrateFullPlanState",
  async () => {
    const response = await api.get<FullStatePayload>(
      API_PATHS.getFullPlanViewStateV2,
    );
    return response.data;
  },
);

function* enumerateFullStatePayload(
  payload: FullStatePayload,
): Generator<{ date: string; filterId: number; data: StateObject }> {
  for (const [filterId, dateMap] of Object.entries(payload)) {
    for (const [date, stateObject] of Object.entries(dateMap)) {
      yield { date, filterId: parseInt(filterId), data: stateObject };
    }
  }
}

export const hydrateFullPlanStateCases = (
  builder: ActionReducerMapBuilder<PlanViewState>,
) => {
  builder.addCase(hydrateFullPlanState.fulfilled, (state, action) => {
    const newState: PlanViewState = {};
    for (const { date, filterId, data } of enumerateFullStatePayload(
      action.payload,
    )) {
      const nexusKey = createPlanViewStateKey("NexusSolution", filterId, date);
      const pondooKey = createPlanViewStateKey(
        "PondooSolution",
        filterId,
        date,
      );

      const activeJobRef = findActiveJobRef(data);

      newState[serializeKey(nexusKey)] = {
        activeJobRef,
        status: determineStatusFromJobAndWebSocketMessages(
          "NexusSolution",
          data,
        ),
        idScenario: data.job?.idScenario,
        job: jobOfRun(data, activeJobRef),
        idSolution: data.planningDate?.nexusIdSolution,
      };

      const pondooData = getPondooValueData(data.websocketMessages, data.job);

      newState[serializeKey(pondooKey)] = {
        activeJobRef,
        status: determineStatusFromJobAndWebSocketMessages(
          "PondooSolution",
          data,
        ),
        idScenario: pondooData.idScenario,
        job: jobOfRun(data, activeJobRef),
        idSolution: data.planningDate?.pondooIdSolution,
      };
    }
    return newState;
  });
};
